import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { PatientAddressDetails, PatientBasicInfo, PatientReviewInfo } from "./patient";
import { AuthenticationSettings } from "@account/interfaces";
import { UserHelperService } from "@main/users/user-helper.service";
import { PlayerOfflineStorage as PatientOfflineStorage } from "@project/player/player-offline.storage";
import { PatientSuggestion } from "@project/models/patient-suggestion";
import { PatientPlaceHolder } from "./patient-placeholder";
import { OrganizationShareReviewed } from "./organization-share-reviewed";
import { AppStorage } from "@bbwt/utils/app-storage";

@Injectable({
    providedIn: "root"
})
export class PatientService extends BaseDataService {
    readonly url = "api/patients";

    private _currentPatientId: string;
    private _currentUserIsPatient: boolean;
    private _currentPatientPlaceHolder: PatientPlaceHolder;
    static readonly CurrentPatientChangedEventName = "CurrentPatientChanged";
    static readonly CurrentPatientLockEventName = "CurrentPatientLock";
    static readonly RequiredPatientSelectionEventName = "RequiredPatientSelection";
    static readonly PatientsUpdatedEventName = "PatientsUpdated";

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        private broadcastService: BroadcastService,
        private userHelperService: UserHelperService
    ) {
        super(http, handlersFactory);
        this.subscribeEvents();
    }

    private subscribeEvents() {
        this.broadcastService.on<AuthenticationSettings>(UserHelperService.ClaimsUpdatedEventName).subscribe(() => {
            if (this.userHelperService.isPatient) {
                this.currentPatientId = this.userHelperService.realPersonId;
                this._currentUserIsPatient = true;
            } else {
                if (this._currentUserIsPatient) {
                    this.currentPatientId = null;
                }
                this._currentUserIsPatient = false;
            }
        });
    }

    get currentPatientId(): string {
        return this._currentPatientId;
    }

    set currentPatientId(id: string) {
        if (!/^[1-9]\d*\-/.test(id)) {
            id = null;
        }

        this._currentPatientId = id;
        AppStorage.setItem("currentPatientId", id);

        this.broadcastService.broadcast(PatientService.CurrentPatientChangedEventName, this._currentPatientId);
    }

    get currentPatientPlaceHolder() {
        if (this.currentPatientId) {
            return this._currentPatientPlaceHolder;
        }
        return null;
    }

    get rawPatientPlaceHolder(): PatientPlaceHolder | null {
        return this._currentPatientPlaceHolder;
    }

    set currentPatientPlaceHolder(data: PatientPlaceHolder) {
        this.updatePatientPlaceHolder(data);
        this.currentPatientId = data.id || data.patientId;
    }

    public updatePatientPlaceHolder(data: PatientPlaceHolder | null) {
        this._currentPatientPlaceHolder = data;

        // Persist immediately (iOS Safari may not reliably fire `beforeunload`).
        // Used by `PatientRequiredGuard` / `patientRestoreResolver` to rehydrate on deep links.
        if (this.userHelperService.isUser) {
            AppStorage.setItem("selectedPatient", data);
        }
    }

    triggerRequiredPatientSelection() {
        this.broadcastService.broadcast(PatientService.RequiredPatientSelectionEventName);
    }

    triggerPatientsUpdated(patientId?: string) {
        this.broadcastService.broadcast(PatientService.PatientsUpdatedEventName, patientId);
    }

    setPatientSelectionLock(isLocked: boolean) {
        this.broadcastService.broadcast(PatientService.CurrentPatientLockEventName, isLocked);
    }

    refreshPatientSelection() {
        this.broadcastService.broadcast(PatientService.CurrentPatientChangedEventName, this._currentPatientId);
    }

    async getAll() {
        const allPatientsInfo = await this.handleRequest(
            this.http.get<{ patients: PatientSuggestion[] }>(`${this.url}/all`),
            this.handlersFactory.getDefault()
        );

        const offlinePatients = PatientOfflineStorage.getAll().map(
            p => <PatientSuggestion>{ id: p.id, fullName: `${p.firstName} ${p.lastName}` }
        );
        allPatientsInfo.patients = offlinePatients.concat(allPatientsInfo.patients).sort();

        return allPatientsInfo;
    }

    async getPatientBasicInfo(id: string): Promise<PatientBasicInfo> {
        if (id) {
            const player: PatientBasicInfo = PatientOfflineStorage.get(id as string);
            return (
                player ??
                (await this.handleRequest<PatientBasicInfo>(
                    this.http.get<PatientBasicInfo>(`${this.url}/${id}`),
                    this.handlersFactory.getDefault()
                ))
            );
        }
    }

    async getPatientSuggestionInfo(id: string): Promise<PatientSuggestion> {
        return await this.handleRequest<PatientSuggestion>(
            this.http.get<PatientSuggestion>(`${this.url}/${id}/get-suggestion-info`),
            this.handlersFactory.getDefault()
        );
    }

    async getPatientAddressDetails(id: string): Promise<PatientAddressDetails> {
        return this.handleRequest<PatientAddressDetails>(
            this.http.get<PatientAddressDetails>(`${this.url}/${id}/get-address-details`),
            this.handlersFactory.getDefault()
        );
    }

    async checkIfPatientNeedsUpdating(id: string): Promise<PatientReviewInfo> {
        return this.handleRequest<PatientReviewInfo>(
            this.http.get<PatientReviewInfo>(`${this.url}/${id}/check-ifNeedsUpdating`),
            this.handlersFactory.getDefault()
        );
    }

    async isAllowedNhsDataExport(id: string): Promise<boolean> {
        return this.handleRequest<boolean>(
            this.http.get<boolean>(`${this.url}/${id}/is-allowed-nhs-export`),
            this.handlersFactory.getDefault()
        );
    }

    async markAsReviewed(id: string): Promise<void> {
        return this.handleRequest<void>(this.http.put<void>(`${this.url}/${id}/mark-reviewed`, null), this.handlersFactory.getDefault());
    }

    async updateConsentState(patientId: string, state: boolean): Promise<void> {
        return this.handleRequest<void>(
            this.http.put<void>(`${this.url}/${patientId}/update-consent-state/${state}`, null),
            this.handlersFactory.getForUpdate("", { successMessage: "Agreement status updated" })
        );
    }

    async updateDoNotRemindSarState(patientId: string, state: boolean): Promise<void> {
        return this.handleRequest<void>(
            this.http.put<void>(`${this.url}/${patientId}/update-do-not-remind-sar-state/${state}`, null),
            this.handlersFactory.getDefault()
        );
    }

    async isMoreThanPatient(masterProfileId: string, checkCurrentOrgOnly?: boolean): Promise<boolean> {
        const url = `${this.url}/${masterProfileId}/has-other-roles${checkCurrentOrgOnly === true ? "?checkCurrentOrgOnly=true" : ""}`;
        return this.handleRequest<boolean>(this.http.get<boolean>(url), this.handlersFactory.getDefault());
    }

    async GetOrganizationsShareReviewed(patientId: string): Promise<OrganizationShareReviewed[]> {
        return this.handleRequest<OrganizationShareReviewed[]>(
            this.http.get<OrganizationShareReviewed[]>(`${this.url}/${patientId}/organizations-share-reviewed`),
            this.handlersFactory.getDefault()
        );
    }

    async updateLoggedInHavingNHSLogin(patientLoginId: string): Promise<void> {
        return this.handleRequest<void>
            (this.http.put<void>(`${this.url}/update-logged-in-having-nhs-login/${patientLoginId}`, null),
            this.handlersFactory.getDefault());
    }

    async sendLoginEmail(patientLoginId: string): Promise<void> {
        return this.handleRequest<void>
            (this.http.put<void>(`${this.url}/send-login-email/${patientLoginId}`, null),
            this.handlersFactory.getDefault());
    }

    async UpdateDataShareReviewedAsync(patientId: string, isFirstNhsLogin: boolean = false): Promise<void> {
        return this.handleRequest<void>
            (this.http.put<void>(`${this.url}/${patientId}/update-organization-data-share-reviewed?isFirstNhsLogin=${isFirstNhsLogin}`, null),
            this.handlersFactory.getDefault());
    }
}
